import { defaultHeaderInterpreter } from '../header/interpreter';
import { CacheRequestInterceptor } from '../interceptors/request';
import { CacheResponseInterceptor } from '../interceptors/response';
import { MemoryAxiosStorage } from '../storage/memory';
import { defaultKeyGenerator } from '../util/key-generator';
/**
 * Apply the caching interceptors for a already created axios instance.
 *
 * @example
 *
 * ```ts
 * import Axios from 'axios';
 * import { setupCache, AxiosCacheInstance } from 'axios-cache-interceptor';
 *
 * // instance will have our custom typings from the return of this function
 * const instance = setupCache(
 *   Axios.create({
 *     // Axios options
 *   }),
 *   {
 *     // Axios-cache-interceptor options
 *   }
 * );
 *
 * // OR
 *
 * const instance = axios.create({
 *   // Axios options
 * }) as AxiosCacheInstance;
 *
 * // As this functions returns the same axios instance but only with
 * // different typings, you can ignore the function return.
 * setupCache(instance, {
 *   // Axios-cache-interceptor options
 * });
 * ```
 *
 * @param axios The already created axios instance
 * @param config The config for the caching interceptors
 * @returns The same instance with better typescript types.
 */
export function setupCache(axios, { storage, generateKey, waiting, headerInterpreter, requestInterceptor, responseInterceptor, ...cacheOptions } = {}) {
    const axiosCache = axios;
    axiosCache.storage = storage || new MemoryAxiosStorage();
    axiosCache.generateKey = generateKey || defaultKeyGenerator;
    axiosCache.waiting = waiting || {};
    axiosCache.headerInterpreter = headerInterpreter || defaultHeaderInterpreter;
    axiosCache.requestInterceptor =
        requestInterceptor || new CacheRequestInterceptor(axiosCache);
    axiosCache.responseInterceptor =
        responseInterceptor || new CacheResponseInterceptor(axiosCache);
    // CacheRequestConfig values
    axiosCache.defaults = {
        ...axios.defaults,
        cache: {
            ttl: 1000 * 60 * 5,
            interpretHeader: false,
            methods: ['get'],
            cachePredicate: { statusCheck: [200, 399] },
            etag: false,
            modifiedSince: false,
            update: {},
            ...cacheOptions
        }
    };
    // Apply interceptors
    axiosCache.requestInterceptor.use();
    axiosCache.responseInterceptor.use();
    return axiosCache;
}
/** @deprecated */
export const useCache = setupCache;
/** @deprecated */
export const createCache = setupCache;
